import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { decksAPI } from '../services/api';
import Layout from '../components/Layout';
import { BookOpen, Languages, Flame, Layers, ArrowRight } from 'lucide-react';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [decks, setDecks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    decksAPI.list()
      .then(r => setDecks(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const totalCards = decks.reduce((a, d) => a + (d.flashcard_count || 0), 0);

  const stats = [
    { label: 'Day Streak', value: user?.streak_count || 0, icon: <Flame size={20} />, color: 'var(--orange)' },
    { label: 'Active Decks', value: decks.length, icon: <BookOpen size={20} />, color: 'var(--accent-soft)' },
    { label: 'Total Cards', value: totalCards, icon: <Layers size={20} />, color: 'var(--green)' },
  ];

  return (
    <Layout>
      <div className="page-header">
        <h1>Welcome back, {user?.username}!</h1>
        <p>What are we learning today?</p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '2.5rem' }}>
        {stats.map((s, i) => (
          <div key={i} style={{
            background: 'var(--bg-card)', border: '1.5px solid var(--border)',
            borderRadius: 'var(--radius)', padding: '1.25rem 1.5rem',
            display: 'flex', alignItems: 'center', gap: '1rem',
            transition: 'all 0.2s', cursor: 'default',
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = s.color; e.currentTarget.style.transform = 'translateY(-2px)'; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; }}
          >
            <div style={{ width: 44, height: 44, borderRadius: 11, display: 'flex', alignItems: 'center', justifyContent: 'center', background: `${s.color}18`, color: s.color, flexShrink: 0 }}>
              {s.icon}
            </div>
            <div>
              <div style={{ fontSize: '1.7rem', fontWeight: 800, color: s.color, lineHeight: 1 }}>{s.value}</div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: '0.2rem', fontWeight: 600 }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Quick actions */}
      <div style={{ marginBottom: '2.5rem' }}>
        <h2 style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
          Quick Actions
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          {[
            { icon: <Languages size={22} />, title: 'New Translation', desc: 'Translate text and save vocabulary', path: '/translate', color: 'var(--accent-soft)' },
            { icon: <BookOpen size={22} />, title: 'My Decks', desc: 'Manage and study your flashcards', path: '/decks', color: 'var(--green)' },
          ].map((a, i) => (
            <button key={i} className="card" style={{ cursor: 'pointer', textAlign: 'left', background: 'var(--bg-card)', border: '1.5px solid var(--border)', transition: 'all 0.2s' }}
              onClick={() => navigate(a.path)}
              onMouseEnter={e => { e.currentTarget.style.borderColor = a.color; e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.3)`; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'none'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              <div style={{ color: a.color, marginBottom: '0.8rem' }}>{a.icon}</div>
              <div style={{ fontWeight: 700, marginBottom: '0.3rem', fontSize: '0.95rem' }}>{a.title}</div>
              <div style={{ fontSize: '0.81rem', color: 'var(--text-muted)' }}>{a.desc}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Recent decks */}
      {!loading && decks.length > 0 && (
        <div>
          <h2 style={{ fontSize: '0.78rem', fontWeight: 700, marginBottom: '1rem', color: 'var(--text-faint)', letterSpacing: '0.12em', textTransform: 'uppercase' }}>
            Recent Decks
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
            {decks.slice(0, 4).map(deck => (
              <div key={deck.id} className="card card-sm"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', transition: 'all 0.15s' }}
                onClick={() => navigate(`/practice/${deck.id}`)}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border-glow)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.92rem' }}>{deck.title}</div>
                  <div style={{ fontSize: '0.77rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    {deck.flashcard_count || 0} cards
                  </div>
                </div>
                <ArrowRight size={15} color="var(--text-faint)" />
              </div>
            ))}
          </div>
        </div>
      )}
    </Layout>
  );
}
